<!-- eslint-disable vue/v-slot-style -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { FormKit } from '@formkit/vue';
import { useBranding } from 'shared/composables/useBranding';

import NextButton from 'dashboard/components-next/button/Button.vue';
import IntegrationsAPI from 'dashboard/api/integrations';

export default {
  components: {
    FormKit,
    NextButton,
  },
  props: {
    integrationId: {
      type: String,
      required: true,
    },
    // when set, the form edits this existing hook's settings instead of creating one
    editHook: {
      type: Object,
      default: null,
    },
  },
  emits: ['close'],
  setup(props) {
    const { integration, isHookTypeInbox } = useIntegrationHook(
      props.integrationId
    );
    const { replaceInstallationName } = useBranding();

    return { integration, isHookTypeInbox, replaceInstallationName };
  },
  data() {
    return {
      endPoint: '',
      alertMessage: '',
      values: {},
      dealFields: [],
      dealFieldsLoading: false,
    };
  },
  created() {
    // pre-fill from the existing hook (the API only returns non-secret settings,
    // so secret fields like api_key/webhook_url stay blank and are kept on save)
    if (this.isEditing && this.editHook.settings) {
      this.values = { ...this.editHook.settings };
    }
    // bitrix24: when the branch source is a deal field, load the field list for the dropdown
    if (
      this.integration.id === 'bitrix24' &&
      this.values.branch_source_type === 'crm_deal_field'
    ) {
      this.fetchDealFields();
    }
  },
  watch: {
    'values.branch_source_type': function onSourceTypeChange(value) {
      if (
        this.integration.id === 'bitrix24' &&
        value === 'crm_deal_field' &&
        !this.dealFields.length &&
        !this.dealFieldsLoading
      ) {
        this.fetchDealFields();
      }
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'integrations/getUIFlags',
      dialogFlowEnabledInboxes: 'inboxes/dialogFlowEnabledInboxes',
    }),
    inboxes() {
      return this.dialogFlowEnabledInboxes
        .filter(inbox => {
          if (!this.isIntegrationDialogflow) {
            return true;
          }
          return !this.connectedDialogflowInboxIds.includes(inbox.id);
        })
        .map(inbox => ({ label: inbox.name, value: inbox.id }));
    },

    connectedDialogflowInboxIds() {
      if (!this.isIntegrationDialogflow) {
        return [];
      }
      return this.integration.hooks.map(hook => hook.inbox?.id);
    },
    isEditing() {
      return Boolean(this.editHook);
    },
    headerTitle() {
      return this.isEditing
        ? `Редактировать: ${this.integration.name}`
        : this.integration.name;
    },
    dealFieldOptions() {
      return this.dealFields.map(field => {
        const values = (field.values || []).map(v => v.value).join(', ');
        const count = field.values_count ?? (field.values || []).length;
        const name =
          field.label && field.label !== field.field ? field.label : field.field;
        return {
          value: field.field,
          label: `${name} · ${count} знач: ${values}`.slice(0, 160),
        };
      });
    },
    formItems() {
      let schema = this.integration.settings_form_schema || [];
      if (this.isEditing) {
        // In edit mode, secret/write-only fields (not in visible_properties) aren't
        // pre-filled — make them optional and hint that leaving them blank keeps the value.
        const visible = new Set(this.integration.visible_properties || []);
        schema = schema.map(item =>
          visible.has(item.name)
            ? item
            : {
                ...item,
                validation: '',
                help: 'Оставьте пустым — текущее значение сохранится',
              }
        );
      }
      // bitrix24: branch_source_value becomes a dropdown of deal fields when the
      // source type is a CRM deal field (options fetched live from the portal).
      if (this.integration.id === 'bitrix24') {
        schema = schema.map(item => {
          if (
            item.name !== 'branch_source_value' ||
            this.values.branch_source_type !== 'crm_deal_field'
          ) {
            return item;
          }
          return {
            ...item,
            type: 'select',
            placeholder: this.dealFieldsLoading
              ? 'Загрузка полей сделки…'
              : 'Выберите поле сделки',
            options: this.dealFieldOptions,
          };
        });
      }
      return schema;
    },
    isIntegrationDialogflow() {
      return this.integration.id === 'dialogflow';
    },
    submitButtonLabel() {
      if (this.integration.id === 'openai' && this.uiFlags.isCreatingHook) {
        return this.$t('INTEGRATION_APPS.ADD.FORM.VALIDATING_OPENAI');
      }
      if (this.isEditing) {
        return 'Сохранить';
      }

      return this.$t('INTEGRATION_APPS.ADD.FORM.SUBMIT');
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    async fetchDealFields() {
      this.dealFieldsLoading = true;
      try {
        const { data } = await IntegrationsAPI.getBitrix24DealFields();
        this.dealFields = data.fields || [];
      } catch (error) {
        this.dealFields = [];
      } finally {
        this.dealFieldsLoading = false;
      }
    },
    buildHookPayload() {
      const hookPayload = {
        app_id: this.integration.id,
        settings: {},
      };

      hookPayload.settings = Object.keys(this.values).reduce((acc, key) => {
        if (key !== 'inbox') {
          acc[key] = this.values[key];
        }
        return acc;
      }, {});

      this.formItems.forEach(item => {
        if (item.validation?.includes('JSON')) {
          hookPayload.settings[item.name] = JSON.parse(
            hookPayload.settings[item.name]
          );
        }
      });

      if (this.isHookTypeInbox && this.values.inbox) {
        hookPayload.inbox_id = this.values.inbox;
      }

      return hookPayload;
    },
    // Edit payload: only non-blank values are sent. The backend merges them onto the
    // existing settings (Integrations::Hook overlay), so blank secret fields (api_key,
    // webhook_url) and internal keys (sync cursor) are preserved.
    buildEditSettings() {
      const settings = {};
      Object.keys(this.values).forEach(key => {
        if (key === 'inbox') return;
        const value = this.values[key];
        if (value === '' || value === null || value === undefined) return;
        settings[key] = value;
      });
      this.formItems.forEach(item => {
        if (item.validation?.includes('JSON') && settings[item.name]) {
          settings[item.name] = JSON.parse(settings[item.name]);
        }
      });
      return settings;
    },
    async submitForm() {
      try {
        if (this.isEditing) {
          await this.$store.dispatch('integrations/updateHook', {
            hookId: this.editHook.id,
            hook: { settings: this.buildEditSettings() },
          });
        } else {
          await this.$store.dispatch(
            'integrations/createHook',
            this.buildHookPayload()
          );
        }
        this.alertMessage = this.$t('INTEGRATION_APPS.ADD.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.response?.data?.message;
        this.alertMessage =
          errorMessage || this.$t('INTEGRATION_APPS.ADD.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto integration-hooks">
    <woot-modal-header
      :header-title="headerTitle"
      :header-content="replaceInstallationName(integration.short_description)"
    />
    <FormKit
      v-model="values"
      type="form"
      form-class="w-full grid gap-4"
      :submit-attrs="{
        inputClass: 'hidden',
        wrapperClass: 'hidden',
      }"
      :incomplete-message="false"
      @submit="submitForm"
    >
      <FormKit v-for="item in formItems" :key="item.name" v-bind="item" />
      <FormKit
        v-if="isHookTypeInbox"
        :options="inboxes"
        type="select"
        name="inbox"
        input-class="reset-base"
        :placeholder="$t('INTEGRATION_APPS.ADD.FORM.INBOX.LABEL')"
        :label="$t('INTEGRATION_APPS.ADD.FORM.INBOX.PLACEHOLDER')"
        validation="required"
        validation-name="Inbox"
      />
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="submitButtonLabel"
          :is-loading="uiFlags.isCreatingHook"
        />
      </div>
    </FormKit>
  </div>
</template>

<style lang="css">
.formkit-outer {
  @apply mt-2;
}

.formkit-form > .formkit-wrapper > ul.formkit-messages {
  @apply hidden;
}

.formkit-form .formkit-help {
  @apply text-n-slate-10 text-sm font-normal mt-2 w-full;
}

/* equivalent of .reset-base */
.formkit-input {
  margin-bottom: 0px !important;
}

[data-invalid] .formkit-message {
  @apply text-n-ruby-9 block text-xs font-normal my-1 w-full;
}

.formkit-outer[data-type='checkbox'] .formkit-wrapper {
  @apply flex items-center gap-2 px-0.5;
}

.formkit-messages {
  @apply list-none m-0 p-0;
}

.formkit-actions {
  @apply hidden;
}
</style>
